/**
 * F1 game version options and tracks per game.
 * Tracks differ between game versions (calendar and bonus circuits).
 * Display names match existing app usage (e.g. Britain, Australia).
 */

export const DEFAULT_GAME_VERSION = 'F126';
export const DEFAULT_GAME_VERSION_CAREER = 'F125';

export const GAME_VERSIONS = [
  { value: 'F126', label: 'F1 26' },
  { value: 'F125', label: 'F1 25' },
  { value: 'F124', label: 'F1 24' },
  { value: 'F123', label: 'F1 23' },
  { value: 'F122', label: 'F1 22' },
  { value: 'F121', label: 'F1 21' },
];

/** F1 26 is not supported for 2 Player Career mode. */
export const GAME_VERSION_EXCLUDED_FROM_CAREER = 'F126';

/**
 * Game version options for race card forms.
 * @param {{ excludeF126?: boolean }} options - Set excludeF126 for 2 Player Career cards
 */
export function getGameVersionOptions({ excludeF126 = false } = {}) {
  if (!excludeF126) return GAME_VERSIONS;
  return GAME_VERSIONS.filter((g) => g.value !== GAME_VERSION_EXCLUDED_FROM_CAREER);
}

/**
 * Tracks available in each F1 game version.
 * F1 26: 2026 calendar — Imola removed, Madrid added at end; 24 circuits + 3 reverse layouts.
 * F1 25: 24 circuits (2025 calendar) + 3 reverse layouts.
 * F1 24: 24 calendar + Portimão (bonus).
 * F1 23: 23 calendar + Portimão, Paul Ricard, Shanghai (bonus).
 * F1 22: 22 calendar (no Russia) + Portimão, Shanghai (post-launch).
 * F1 21: 21 at launch + Jeddah (update); includes Sochi, Portimão, Paul Ricard, Shanghai.
 */
export const TRACKS_BY_GAME = {
  F126: [
    'Australia',
    'China',
    'Japan',
    'Bahrain',
    'Saudi Arabia',
    'Miami',
    'Monaco',
    'Spain',
    'Canada',
    'Austria',
    'Britain',
    'Belgium',
    'Hungary',
    'Netherlands',
    'Monza',
    'Azerbaijan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Las Vegas',
    'Qatar',
    'Abu Dhabi',
    'Madrid',
    'Austria (Reverse)',
    'Britain (Reverse)',
    'Netherlands (Reverse)',
  ],
  F125: [
    'Australia',
    'China',
    'Japan',
    'Bahrain',
    'Saudi Arabia',
    'Miami',
    'Imola',
    'Monaco',
    'Spain',
    'Canada',
    'Austria',
    'Britain',
    'Belgium',
    'Hungary',
    'Netherlands',
    'Monza',
    'Azerbaijan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Las Vegas',
    'Qatar',
    'Abu Dhabi',
    'Austria (Reverse)',
    'Britain (Reverse)',
    'Netherlands (Reverse)',
  ],
  F124: [
    'Australia',
    'China',
    'Japan',
    'Bahrain',
    'Saudi Arabia',
    'Miami',
    'Imola',
    'Monaco',
    'Spain',
    'Canada',
    'Austria',
    'Britain',
    'Belgium',
    'Hungary',
    'Netherlands',
    'Monza',
    'Azerbaijan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Las Vegas',
    'Qatar',
    'Abu Dhabi',
    'Portugal', // Portimão
  ],
  F123: [
    'Australia',
    'China', // bonus in F1 23
    'Japan',
    'Bahrain',
    'Saudi Arabia',
    'Miami',
    'Imola',
    'Monaco',
    'Spain',
    'Canada',
    'Austria',
    'Britain',
    'Belgium',
    'Hungary',
    'Netherlands',
    'Monza',
    'Azerbaijan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Las Vegas',
    'Qatar',
    'Abu Dhabi',
    'Portugal', // Portimão
    'France',   // Paul Ricard
  ],
  F122: [
    'Australia',
    'China', // added post-launch
    'Japan',
    'Bahrain',
    'Saudi Arabia',
    'Miami',
    'Imola',
    'Monaco',
    'Spain',
    'Canada',
    'Austria',
    'Britain',
    'Belgium',
    'Hungary',
    'Netherlands',
    'Monza',
    'Azerbaijan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Abu Dhabi',
    'Portugal', // Portimão post-launch
    'France',   // Paul Ricard
  ],
  F121: [
    'Australia',
    'Bahrain',
    'Imola',
    'Portugal', // Portimão
    'Spain',
    'Monaco',
    'Azerbaijan',
    'Canada',
    'France',   // Paul Ricard
    'Austria',
    'Britain',
    'Hungary',
    'Belgium',
    'Netherlands',
    'Monza',
    'China',    // Shanghai
    'Russia',   // Sochi
    'Japan',
    'Singapore',
    'Austin',
    'Mexico',
    'Brazil',
    'Abu Dhabi',
    'Saudi Arabia', // Jeddah (added via update)
  ],
};

/**
 * Returns the list of track names for a given game version.
 * @param {string} gameVersion - One of 'F126', 'F125', 'F124', 'F123', 'F122', 'F121'
 * @returns {string[]}
 */
export function getTracksForGame(gameVersion) {
  return TRACKS_BY_GAME[gameVersion] || TRACKS_BY_GAME.F126;
}

/**
 * All unique track names across all games (for filters / fallback).
 * Order: F126 list first, then any extras from other games.
 */
const f126Set = new Set(TRACKS_BY_GAME.F126);
const allTracks = [...TRACKS_BY_GAME.F126];
[TRACKS_BY_GAME.F125, TRACKS_BY_GAME.F124, TRACKS_BY_GAME.F123, TRACKS_BY_GAME.F122, TRACKS_BY_GAME.F121].forEach((list) => {
  list.forEach((t) => {
    if (!f126Set.has(t)) {
      f126Set.add(t);
      allTracks.push(t);
    }
  });
});

export const ALL_TRACK_OPTIONS = allTracks;
